// FILE: client/src/pages/WatchParty.js
import { useEffect, useRef, useState, useCallback } from "react";
import io from "socket.io-client";
import { useNavigate, useParams } from "react-router-dom";

import logo from "../assets/main-logo.png";
import Home from "./Home";
import Room from "./Room";
// eslint-disable-next-line no-unused-vars
import InstallPWAButton from "../components/InstallPWAButton";

// ✅ CHANGE THIS if your canonical share domain is different
const PUBLIC_WEB_ORIGIN = "https://www.watchpartyrooms.com";



// ✅ Capacitor-safe socket URL selection:
// - Capacitor app often runs at "capacitor://localhost" (hostname looks like "localhost")
// - so NEVER use http://localhost:3001 when running inside the native app
const isRunningInCapacitorNative = () => {
  try {
    const cap = window.Capacitor;
    if (!cap) return false;

    // Capacitor: getPlatform() returns "web" | "ios" | "android"
    if (typeof cap.getPlatform === "function") {
      return cap.getPlatform() !== "web";
    }

    return true;
  } catch {
    return false;
  }
};

const getSocketUrl = () => {
  // ✅ Optional override (works in Capacitor too)
  // localStorage.setItem("wp_api_override", "http://192.168.x.x:3001")
  try {
    const saved = (localStorage.getItem("wp_api_override") || "").trim();
    if (saved) return saved;
  } catch {
    // ignore
  }

  // ✅ If running as a native Capacitor app, always use production
  if (isRunningInCapacitorNative()) {
    return "https://api.watchpartyrooms.com";
  }

  const host = (window.location.hostname || "").toLowerCase();
  const protocol = (window.location.protocol || "").toLowerCase();

  // ✅ Only use local server for real desktop localhost dev
  if (
    (host === "localhost" || host === "127.0.0.1") &&
    (protocol === "http:" || protocol === "https:")
  ) {
    return "http://localhost:3001";
  }

  // ✅ Default: production
  return "https://api.watchpartyrooms.com";
};

const SOCKET_URL = getSocketUrl();

// ✅ Force transports to help WebView environments
const socket = io(SOCKET_URL, { transports: ["websocket", "polling"] });

function getYouTubeId(url) {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);

    if (u.hostname.includes("youtu.be")) return parts[0] || null;
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    if (u.pathname.includes("/embed/")) {
      return u.pathname.split("/embed/")[1]?.split(/[?&/]/)[0] || null;
    }
    if (u.pathname.includes("/live/")) {
      return u.pathname.split("/live/")[1]?.split(/[?&/]/)[0] || null;
    }

    return null;
  } catch {
    return null;
  }
}

function getTwitchChannel(url) {
  try {
    const u = new URL(url);
    const host = (u.hostname || "").toLowerCase();

    if (host.endsWith("twitch.tv")) {
      const parts = u.pathname.split("/").filter(Boolean);
      const channel = parts[0] || null;
      if (!channel) return null;

      const blocked = new Set(["directory", "videos", "p", "settings", "login", "signup"]);
      if (blocked.has(channel.toLowerCase())) return null;

      return channel;
    }

    return null;
  } catch {
    return null;
  }
}

function getKickChannel(url) {
  try {
    const u = new URL(url);
    const host = (u.hostname || "").toLowerCase();

    if (host.endsWith("kick.com")) {
      const parts = u.pathname.split("/").filter(Boolean);
      const channel = parts[0] || null;
      if (!channel) return null;

      const blocked = new Set(["categories", "browse", "login", "signup"]);
      if (blocked.has(channel.toLowerCase())) return null;

      return channel;
    }

    if (host === "player.kick.com") {
      const parts = u.pathname.split("/").filter(Boolean);
      return parts[0] || null;
    }

    return null;
  } catch {
    return null;
  }
}

function classifyLink(url) {
  try {
    const u = new URL(url);
    const host = (u.hostname || "").toLowerCase();

    // VIDEO FIRST
    if (getYouTubeId(url)) return { type: "video", subtype: "youtube" };
    if (getTwitchChannel(url)) return { type: "video", subtype: "twitch" };
    if (getKickChannel(url)) return { type: "video", subtype: "kick" };
    if (url.toLowerCase().endsWith(".mp4")) return { type: "video", subtype: "mp4" };

    // SHOPPING / PRODUCT
    if (
      host.includes("amazon") ||
      host.includes("ebay") ||
      host.includes("walmart") ||
      host.includes("target") ||
      host.includes("shop") ||
      host.includes("store") ||
      host.includes("product")
    ) {
      return { type: "shopping" };
    }

    // Fallback → treat as shopping (safer than breaking video)
    return { type: "shopping" };
  } catch {
    return { type: "unknown" };
  }
}

function normalizeRoomCode(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";
  const noPath = s.replace(/^\/?room\//, "").replace(/^\//, "");
  const cleaned = noPath.replace(/[^a-z0-9_-]/g, "");
  return cleaned;
}

function Toast({ toast, onClose }) {
  if (!toast?.open) return null;

  const kind = toast.kind || "info";

  const bg =
    kind === "success"
      ? "rgba(16, 185, 129, 0.14)"
      : kind === "error"
      ? "rgba(239, 68, 68, 0.14)"
      : "rgba(124, 58, 237, 0.14)";

  const border =
    kind === "success"
      ? "rgba(16, 185, 129, 0.35)"
      : kind === "error"
      ? "rgba(239, 68, 68, 0.35)"
      : "rgba(124, 58, 237, 0.35)";

  const dot =
    kind === "success" ? "#10b981" : kind === "error" ? "#ef4444" : "var(--accent-2)";

  return (
    <div
      style={{
        position: "fixed",
        top: "calc(env(safe-area-inset-top) + 72px)",
        right: 16,
        zIndex: 9999,
        width: 360,
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <div
        role="status"
        aria-live="polite"
        style={{
          background: `linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03)), ${bg}`,
          border: `1px solid ${border}`,
          borderRadius: 16,
          boxShadow: "0 18px 60px rgba(0,0,0,0.55)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          padding: 12,
          color: "var(--text)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              marginTop: 4,
              background: dot,
              boxShadow: "0 0 0 3px rgba(255,255,255,0.06)",
              flex: "0 0 auto",
            }}
          />
          <div style={{ flex: "1 1 auto" }}>
            <div style={{ fontWeight: 900, letterSpacing: 0.2, marginBottom: 2 }}>
              {kind === "success" ? "Done" : kind === "error" ? "Heads up" : "Info"}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.35, color: "rgba(255,255,255,0.92)" }}>
              {toast.text}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-small"
            style={{
              padding: "6px 10px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              color: "var(--text)",
              cursor: "pointer",
              fontWeight: 900,
              flex: "0 0 auto",
            }}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WatchParty() {
  const [wasKicked, setWasKicked] = useState(false);
  const mp4Ref = useRef(null);
  const ytPlayerRef = useRef(null);

  const isIOS = (() => {
    try {
      const ua = navigator.userAgent || "";
      return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    } catch {
      return false;
    }
  })();

  const rehydrateHostRef = useRef(false);
  const suppressHostEmitRef = useRef(false);

  const navigate = useNavigate();
  const { roomId } = useParams();

  const [name, setName] = useState("");
  const [room, setRoom] = useState(normalizeRoomCode(roomId || ""));
  const [joined, setJoined] = useState(false);

  const [isHost, setIsHost] = useState(false);
  const [hostId, setHostId] = useState(null);
  const [hostName, setHostName] = useState(null);

  const [inputUrl, setInputUrl] = useState("");
  const [video, setVideo] = useState(null);

  const [lastHostRequest, setLastHostRequest] = useState(null);
  const [requestSent, setRequestSent] = useState(false);

  const [mp4Ready, setMp4Ready] = useState(false);
  const [ytReady, setYtReady] = useState(false);

  const [pendingSync, setPendingSync] = useState(null); // { time, shouldPlay }
  const [roomToken, setRoomToken] = useState(null);

  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const chatEndRef = useRef(null);

  const [mutedUsers, setMutedUsers] = useState({});
  const isSelfMuted = !!(socket?.id && mutedUsers[socket.id]);
  const [userNames, setUserNames] = useState({});
  const [roomMembers, setRoomMembers] = useState([]);

  const [unmuteRequestSentAt, setUnmuteRequestSentAt] = useState(0);
  const [unmuteStatusText, setUnmuteStatusText] = useState("");
  const [unmuteRequests, setUnmuteRequests] = useState([]);

  const [showHostDrawer, setShowHostDrawer] = useState(false);

  // Toast
  const [toast, setToast] = useState({ open: false, text: "", kind: "info" });
  const toastTimerRef = useRef(null);

  const showToast = (text, kind = "info", ms = 2200) => {
    try {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    } catch {
      // ignore
    }

    setToast({ open: true, text: String(text || ""), kind });

    toastTimerRef.current = setTimeout(() => {
      setToast((t) => ({ ...t, open: false }));
    }, ms);
  };

  const closeToast = () => {
    try {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    } catch {
      // ignore
    }
    setToast((t) => ({ ...t, open: false }));
  };

  useEffect(() => {
    return () => {
      try {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      } catch {
        // ignore
      }
    };
  }, []);

  // ✅ Share origin helper
  const getShareOrigin = () => {
    try {
      const host = window.location.hostname;

      // Capacitor app should share PUBLIC link
      if (isRunningInCapacitorNative()) return PUBLIC_WEB_ORIGIN;

      // Local dev stays local
      if (host === "localhost" || host === "127.0.0.1") return window.location.origin;

      // If you're running on a LAN IP, don't share that — share public
      if (host.startsWith("192.168.") || host.startsWith("10.") || host.startsWith("172.")) {
        return PUBLIC_WEB_ORIGIN;
      }

      // Everything else (prod) is already the public origin anyway
      return window.location.origin;
    } catch {
      return PUBLIC_WEB_ORIGIN;
    }
  };

  // ✅ YouTube needs a stable HTTPS origin
  const getYouTubeOrigin = () => {
    try {
      return window.location.origin || PUBLIC_WEB_ORIGIN;
    } catch {
      return PUBLIC_WEB_ORIGIN;
    }
  };

  // ✅ Persistent user identity
  const userIdStorageKey = "wp_user_id";

  const getOrCreateUserId = () => {
    try {
      const existing = (localStorage.getItem(userIdStorageKey) || "").trim();
      if (existing) return existing;

      const fresh = `${Date.now()}_${Math.random().toString(16).slice(2)}_${Math.random()
        .toString(16)
        .slice(2)}`;

      localStorage.setItem(userIdStorageKey, fresh);
      return fresh;
    } catch {
      return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
  };

  const userIdRef = useRef(getOrCreateUserId());

  // URL token (invite)
  const urlToken = (() => {
    try {
      return new URLSearchParams(window.location.search).get("token") || null;
    } catch {
      return null;
    }
  })();

  // Persisted name
  const readSavedName = () => {
    try {
      const session = (sessionStorage.getItem("wp_session_name") || "").trim();
      if (session) return session;

      return (localStorage.getItem("wp_name") || "").trim() || null;
    } catch {
      return null;
    }
  };

  const saveName = (nextName) => {
    try {
      const n = String(nextName || "").trim();
      if (!n) return;

      sessionStorage.setItem("wp_session_name", n);
      localStorage.setItem("wp_name", n);
    } catch {
      // ignore
    }
  };

  // Persist room token
  const tokenKeyForRoom = (r) => `wp_room_token_${String(r || "").trim().toLowerCase()}`;

  const readSavedToken = useCallback((r) => {
    try {
      return (localStorage.getItem(tokenKeyForRoom(r)) || "").trim() || null;
    } catch {
      return null;
    }
  }, []);

  const saveToken = (r, t) => {
    try {
      if (!t) return;
      localStorage.setItem(tokenKeyForRoom(r), String(t).trim());
    } catch {
      // ignore
    }
  };

  const clearToken = (r) => {
    try {
      localStorage.removeItem(tokenKeyForRoom(r));
    } catch {
      // basic
    }
  };

  // ✅ SAFETY: destroy YouTube player without crashing React unmount
  const safeDestroyYouTubePlayer = useCallback(() => {
    // 1) Destroy player object safely
    try {
      if (ytPlayerRef.current && typeof ytPlayerRef.current.destroy === "function") {
        ytPlayerRef.current.destroy();
      }
    } catch (e) {
      try {
        // eslint-disable-next-line no-console
        console.warn("YT destroy cleanup error:", e);
      } catch {
        // ignore
      }
    } finally {
      ytPlayerRef.current = null;
      setYtReady(false);
    }

    // 2) Clear DOM inside container so React doesn't fight leftover iframe nodes
    try {
      const el = document.getElementById("yt-player");
      if (el) el.innerHTML = "";
    } catch {
      // ignore
    }
  }, []);

  // ✅ Central exit (used by back arrow + future Exit button)
const exitRoom = (opts = { navigateHome: true }) => {
  const safeRoom = normalizeRoomCode(room || roomId || "");

  if (safeRoom) {
    try {
      socket.emit("leave-room", { room: safeRoom });
    } catch {
      // ignore
    }
  }

  // 🔴 ALWAYS clear room state
  setRoom("");
  setRoomToken(null);
  setInputUrl("");

  setJoined(false);
  setIsHost(false);
  setHostId(null);
  setHostName(null);
  setVideo(null);
  setChatMessages([]);
  setMutedUsers({});
  setUnmuteRequests([]);
  setUnmuteStatusText("");
  setRoomMembers([]);
  setShowHostDrawer(false);
  setRequestSent(false);
  setLastHostRequest(null);
  setPendingSync(null);
  setMp4Ready(false);
  setYtReady(false);

  // cleanup player
  safeDestroyYouTubePlayer();

  if (opts?.navigateHome) {
    try {
      navigate("/");
    } catch {
      // ignore
    }
  }
};

  // ✅ Fix: if URL goes back to "/" (roomId disappears) but joined=true, exit immediately
  useEffect(() => {
    const urlRoom = normalizeRoomCode(roomId || "");
    if (!urlRoom && joined) {
      exitRoom({ navigateHome: false }); // already navigating / is home
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Save invite token early
  useEffect(() => {
    const urlRoom = normalizeRoomCode(roomId || "");
    if (!urlRoom) return;
    if (!urlToken) return;
    saveToken(urlRoom, urlToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Auto-join when navigating directly to /room/:roomId
  useEffect(() => {
    
      if (wasKicked) return; // 🔥 THIS LINE

    const urlRoom = normalizeRoomCode(roomId || "");
    if (!urlRoom) return;

    const savedName = readSavedName();
    if (!savedName) return;

    const hasSessionName = (() => {
      try {
        return !!(sessionStorage.getItem("wp_session_name") || "").trim();
      } catch {
        return false;
      }
    })();

    if (urlToken && !hasSessionName) {
      setRoom(urlRoom);
      return;
    }

    setName(savedName);
    setRoom(urlRoom);

    if (urlToken) saveToken(urlRoom, urlToken);

    setJoined(true);

     socket.emit("join-room", {
      room: urlRoom,
      name: savedName,
      token: (urlToken || readSavedToken(urlRoom)) || undefined,
      userId: userIdRef.current,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  useEffect(() => {
    const trimmed = (name || "").trim();
    if (trimmed) saveName(trimmed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  useEffect(() => {
    if (window.YT && window.YT.Player) return;
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  }, []);

  // ✅ cleanup YT player when switching AWAY from YouTube (prevents React removeChild crash)
  useEffect(() => {
    if (video?.type === "youtube") return;

    // if current video is not youtube, ensure any old YT player is fully cleaned up
    safeDestroyYouTubePlayer();
  }, [video?.type, safeDestroyYouTubePlayer]);

  // ✅ final cleanup on unmount
  useEffect(() => {
    return () => {
      safeDestroyYouTubePlayer();
    };
  }, [safeDestroyYouTubePlayer]);

  const isPlayerReady = () => {
    if (!video) return false;
    if (video.type === "mp4") return mp4Ready;
    if (video.type === "youtube") return ytReady;
    return false;
  };

  const getCurrentTime = () => {
    if (!video) return 0;
    if (video.type === "mp4" && mp4Ref.current) return mp4Ref.current.currentTime || 0;
    if (video.type === "youtube" && ytPlayerRef.current?.getCurrentTime)
      return ytPlayerRef.current.getCurrentTime() || 0;
    return 0;
  };

  const seekTo = (time) => {
    if (!video) return;
    if (video.type === "mp4" && mp4Ref.current) mp4Ref.current.currentTime = time;
    if (video.type === "youtube" && ytPlayerRef.current?.seekTo)
      ytPlayerRef.current.seekTo(time, true);
  };

  const getLocalIsPlaying = () => {
    if (!video) return false;

    if (video.type === "mp4" && mp4Ref.current) {
      return !mp4Ref.current.paused;
    }

    if (video.type === "youtube" && ytPlayerRef.current?.getPlayerState) {
      try {
        return ytPlayerRef.current.getPlayerState() === 1;
      } catch {
        return false;
      }
    }

    return false;
  };

  const playLocal = () => {
    if (!video) return;

    if (video.type === "mp4" && mp4Ref.current) {
      mp4Ref.current.play();
      return;
    }

    if (video.type === "youtube" && ytPlayerRef.current?.playVideo) {
      ytPlayerRef.current.playVideo();
    }
  };

  const pauseLocal = () => {
    if (!video) return;
    if (video.type === "mp4" && mp4Ref.current) mp4Ref.current.pause();
    if (video.type === "youtube" && ytPlayerRef.current?.pauseVideo)
      ytPlayerRef.current.pauseVideo();
  };

  const viewerTapToStart = () => {
    if (!video) return;

    if (video.type === "mp4" && mp4Ref.current) {
      try {
        mp4Ref.current.play();
      } catch {
        // ignore
      }
      return;
    }

    if (video.type === "youtube" && ytPlayerRef.current?.playVideo) {
      try {
        if (ytPlayerRef.current?.unMute) ytPlayerRef.current.unMute();
        if (ytPlayerRef.current?.setVolume) ytPlayerRef.current.setVolume(100);
      } catch {
        // ignore
      }

      try {
        ytPlayerRef.current.playVideo();
      } catch {
        // ignore
      }
    }
  };

  // ✅ Do NOT scroll the page when new chat messages arrive.
  // ChatPane itself can handle internal chat scrolling if needed.
  useEffect(() => {
    // intentionally blank
  }, [chatMessages, joined]);

  const sendChat = () => {
    if (!joined) return;

    if (isSelfMuted) {
      setUnmuteStatusText("You are muted - messages won't send to the room.");
      setTimeout(() => setUnmuteStatusText(""), 2000);
      return;
    }

    const msg = chatInput.trim();
    if (!msg) return;

    socket.emit("chat-message", { room, text: msg });
    setChatInput("");
  };

  const muteUser = (targetId, durationMs) => {
    if (!isHost) return;
    if (!targetId) return;
    socket.emit("mute-user", { room, targetId, durationMs });
  };

  const unmuteUser = (targetId) => {
    if (!isHost) return;
    if (!targetId) return;
    socket.emit("unmute-user", { room, targetId });
  };

const requestUnmute = () => {
  if (!joined) return;

  const now = Date.now();
  if (now - unmuteRequestSentAt < 5000) {
    setUnmuteStatusText("Slow down—try again in a few seconds.");
    setTimeout(() => setUnmuteStatusText(""), 2000);
    return;
  }

  setUnmuteRequestSentAt(now);
  socket.emit("request-unmute", { room });
  setUnmuteStatusText("Unmute request sent to host.");
  setTimeout(() => setUnmuteStatusText(""), 2500);
};

  const approveUnmuteRequest = (requesterId) => {
    if (!isHost) return;
    if (!requesterId) return;

    socket.emit("unmute-user", { room, targetId: requesterId });
    setUnmuteRequests((prev) => prev.filter((r) => r.requesterId !== requesterId));
  };

  const ignoreUnmuteRequest = (requesterId) => {
    setUnmuteRequests((prev) => prev.filter((r) => r.requesterId !== requesterId));
  };

  const kickUser = (targetId) => {
    if (!isHost) return;
    if (!targetId) return;
    socket.emit("kick-user", { room, targetId });
  };

  const mutedRoster = (() => {
    if (!isHost) return [];
    return Object.keys(mutedUsers)
      .filter((id) => mutedUsers[id])
      .filter((id) => id !== socket.id)
      .map((id) => ({
        id,
        name: userNames[id] || "Someone",
      }));
  })();

  useEffect(() => {
    if (!pendingSync) return;
    if (!video) return;
    if (!isPlayerReady()) return;

    seekTo(pendingSync.time);
    if (pendingSync.shouldPlay) playLocal();
    else pauseLocal();

    setPendingSync(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSync, mp4Ready, ytReady, video]);

  const normalizeSyncPayload = (payload) => {
    if (typeof payload === "number") return { time: payload, serverSentMs: Date.now() };
    return {
      time: payload?.time ?? 0,
      serverSentMs: payload?.serverSentMs ?? Date.now(),
    };
  };

  const adjustedPlayTime = (time, serverSentMs) => {
    const delaySec = (Date.now() - serverSentMs) / 1000;
    return time + Math.max(0, delaySec);
  };

  // Auto re-join on reconnect
  useEffect(() => {
   const onConnect = () => {
  if (!room || !joined || wasKicked) return;

  const safeRoom = normalizeRoomCode(room);

  socket.emit("join-room", {
    room: safeRoom,
    name,
    token: readSavedToken(safeRoom),
    userId: userIdRef.current,
  });
};

    socket.on("connect", onConnect);
    return () => socket.off("connect", onConnect);
 }, [room, joined, name, readSavedToken, wasKicked]);
     useEffect(() => {
  if (!socket || !room || wasKicked) return;

  socket.emit("request-sync", { room });

  const retry = setTimeout(() => {
    socket.emit("request-sync", { room });
  }, 500);

  return () => clearTimeout(retry);
}, [room, wasKicked]);
  // ✅ Socket listeners
  useEffect(() => {
    const onRole = ({ isHost }) => {
      const nextIsHost = !!isHost;
      setIsHost(nextIsHost);

      if (nextIsHost) {
        setRequestSent(false);
        rehydrateHostRef.current = true;
        socket.emit("request-sync", { room });
      }
    };

       const onHostChanged = ({ hostId, hostName }) => {
           setHostId(hostId);
           setHostName(hostName);
           setRequestSent(false);

            socket.emit("request-sync", { room });
           };

    const onHostRequested = ({ requesterId, requesterName }) => {
      setLastHostRequest({ id: requesterId, name: requesterName || "Someone" });
    };

    const onRoomToken = ({ token }) => {
      const t = token || null;
      setRoomToken(t);

      const r = normalizeRoomCode(room || roomId || "");
      if (t && r) saveToken(r, t);
    };

    const onJoinDenied = ({ message }) => {
      showToast(message || "Invite link required to join this room.", "error", 3200);

      if (room) clearToken(room);

      // reset state, then home
      
    };

    const onChatMuted = ({ message }) => {
      showToast(message || "You are muted by the host.", "error", 2800);
    };

    const onMuted = ({ muted, room: msgRoom }) => {
      if (msgRoom && msgRoom !== room) return;
      if (!socket.id) return;

      setMutedUsers((prev) => {
        const next = { ...prev };
        if (muted) next[socket.id] = true;
        else delete next[socket.id];
        return next;
      });
    };

    const onMutedRosterUpdate = ({ room: msgRoom, targetId, muted, name }) => {
      if (msgRoom && msgRoom !== room) return;
      if (!isHost) return;
      if (!targetId) return;

      setMutedUsers((prev) => {
        const next = { ...prev };
        if (muted) next[targetId] = true;
        else delete next[targetId];
        return next;
      });

      if (name) {
        setUserNames((prev) => ({
          ...prev,
          [targetId]: name,
        }));
      }
    };

    const onMutedRosterSnapshot = ({ room: msgRoom, muted }) => {
      if (msgRoom && msgRoom !== room) return;
      if (!isHost) return;

      const list = Array.isArray(muted) ? muted : [];

      setMutedUsers((prev) => {
        const next = { ...prev };
        for (const id of Object.keys(next)) {
          if (id !== socket.id) delete next[id];
        }
        for (const item of list) {
          if (item?.targetId) next[item.targetId] = true;
        }
        return next;
      });

      setUserNames((prev) => {
        const next = { ...prev };
        for (const item of list) {
          if (item?.targetId)
            next[item.targetId] = item.name || next[item.targetId] || "Someone";
        }
        return next;
      });
    };

    const onMembersSnapshot = ({ room: msgRoom, members }) => {
      if (msgRoom && msgRoom !== room) return;
      if (!isHost) return;

      const list = Array.isArray(members) ? members : [];
      setRoomMembers(
        list.filter((m) => m && m.id).map((m) => ({ id: m.id, name: m.name || "Someone" }))
      );

      setUserNames((prev) => {
        const next = { ...prev };
        for (const m of list) {
          if (m?.id) next[m.id] = m.name || next[m.id] || "Someone";
        }
        return next;
      });
    };

    const onMemberJoined = ({ room: msgRoom, id, name }) => {
      if (msgRoom && msgRoom !== room) return;
      if (!isHost) return;
      if (!id) return;

      setRoomMembers((prev) => {
        if (prev.some((m) => m.id === id)) return prev;
        return [...prev, { id, name: name || "Someone" }];
      });

      setUserNames((prev) => ({
        ...prev,
        [id]: name || prev[id] || "Someone",
      }));
    };

    const onMemberLeft = ({ room: msgRoom, id }) => {
      if (msgRoom && msgRoom !== room) return;
      if (!isHost) return;
      if (!id) return;

      setRoomMembers((prev) => prev.filter((m) => m.id !== id));

      setMutedUsers((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };

        const onKicked = ({ message }) => {
  setWasKicked(true); // 🔥 THIS IS THE KEY

  showToast(message || "You were removed by the host", "error");

  // clear token so they can't rejoin
  try {
    const safeRoom = normalizeRoomCode(room || roomId || "");
    localStorage.removeItem(`wp_room_token_${safeRoom}`);
  } catch {}

  exitRoom({ navigateHome: true });
};
    const onUnmuteRequested = ({ requesterId, requesterName, ts, room: msgRoom }) => {
      if (msgRoom && msgRoom !== room) return;
       showToast(`${requesterName} wants to be unmuted`, "success", 3000);
      setUnmuteRequests((prev) => {
        if (prev.some((r) => r.requesterId === requesterId)) return prev;
        return [
          {
            requesterId,
            requesterName: requesterName || "Someone",
            ts: ts || Date.now(),
          },
          ...prev,
        ];
      });

      if (requesterId) {
        setUserNames((prev) => ({
          ...prev,
          [requesterId]: requesterName || prev[requesterId] || "Someone",
        }));
      }
    };

    const onUnmuteRequestStatus = ({ ok, reason }) => {
      if (ok) return;
      if (reason === "cooldown") setUnmuteStatusText("Slow down—try again in a few seconds.");
      else if (reason === "no-host") setUnmuteStatusText("No host found in the room.");
      else if (reason === "not-muted") setUnmuteStatusText("You aren’t muted.");
      else setUnmuteStatusText("Could not send unmute request.");
      setTimeout(() => setUnmuteStatusText(""), 2500);
    };

    const onSetVideo = (v) => {
      setVideo(v);
      setMp4Ready(false);
      setYtReady(false);
      setPendingSync(null);
    };

    const onSyncPlay = (payload) => {
      if (video?.type === "twitch" || video?.type === "kick") return;
      if (isHost && !rehydrateHostRef.current) return;

      const { time, serverSentMs } = normalizeSyncPayload(payload);
      const t = adjustedPlayTime(time, serverSentMs);

      if (isHost) suppressHostEmitRef.current = true;

      if (isPlayerReady()) {
        seekTo(t);
        playLocal();
        setPendingSync(null);
      } else {
        setPendingSync({ time: t, shouldPlay: true });
      }

      if (isHost) {
        setTimeout(() => {
          suppressHostEmitRef.current = false;
          rehydrateHostRef.current = false;
        }, 700);
      }
    };

    const onSyncPause = (payload) => {
      if (video?.type === "twitch" || video?.type === "kick") return;
      if (isHost && !rehydrateHostRef.current) return;

      const { time } = normalizeSyncPayload(payload);

      if (isHost) suppressHostEmitRef.current = true;

      if (isPlayerReady()) {
        seekTo(time);
        pauseLocal();
        setPendingSync(null);
      } else {
        setPendingSync({ time, shouldPlay: false });
      }

      if (isHost) {
        setTimeout(() => {
          suppressHostEmitRef.current = false;
          rehydrateHostRef.current = false;
        }, 700);
      }
    };

    const onSyncSeek = (payload) => {
      if (video?.type === "twitch" || video?.type === "kick") return;
      if (isHost && !rehydrateHostRef.current) return;

      const { time } = normalizeSyncPayload(payload);

      if (isHost) suppressHostEmitRef.current = true;

      if (isPlayerReady()) seekTo(time);
      else setPendingSync((prev) => ({ time, shouldPlay: prev?.shouldPlay ?? false }));

      if (isHost) {
        setTimeout(() => {
          suppressHostEmitRef.current = false;
        }, 700);
      }
    };

    // ✅ NEW: chat snapshot on join/reconnect (refresh persistence)
    const onChatSnapshot = (history) => {
      const list = Array.isArray(history) ? history : [];

      // update names map too (so roster chips/labels look right)
      setUserNames((prev) => {
        const next = { ...prev };
        for (const m of list) {
          if (m?.id) next[m.id] = m.name || next[m.id] || "Someone";
        }
        return next;
      });

      setChatMessages(
        list.map((m) => ({
          id: m?.id || null,
          name: m?.name || "Someone",
          text: m?.text || "",
          ts: m?.ts || Date.now(),
        }))
      );
    };

    const onChatMessage = (m) => {
      if (!m) return;

      if (m.id) {
        setUserNames((prev) => ({
          ...prev,
          [m.id]: m.name || prev[m.id] || "Someone",
        }));
      }

      setChatMessages((prev) => [
        ...prev,
        {
          id: m.id,
          name: m.name || "Someone",
          text: m.text || "",
          ts: m.ts || Date.now(),
        },
      ]);
    };

    socket.on("role", onRole);
    socket.on("host-changed", onHostChanged);
    socket.on("host-requested", onHostRequested);

    socket.on("room-token", onRoomToken);
    socket.on("join-denied", onJoinDenied);

    socket.on("chat-muted", onChatMuted);
    socket.on("muted", onMuted);

    socket.on("muted-roster-update", onMutedRosterUpdate);
    socket.on("muted-roster-snapshot", onMutedRosterSnapshot);

    socket.on("members-snapshot", onMembersSnapshot);
    socket.on("member-joined", onMemberJoined);
    socket.on("member-left", onMemberLeft);

    socket.on("kicked", onKicked);

    socket.on("unmute-requested", onUnmuteRequested);
    socket.on("unmute-request-status", onUnmuteRequestStatus);

    socket.on("set-video", onSetVideo);
    socket.on("sync-play", onSyncPlay);
    socket.on("sync-pause", onSyncPause);
    socket.on("sync-seek", onSyncSeek);

    // ✅ NEW: snapshot listener
    socket.on("chat-snapshot", onChatSnapshot);

    socket.on("chat-message", onChatMessage);

    return () => {
      socket.off("role", onRole);
      socket.off("host-changed", onHostChanged);
      socket.off("host-requested", onHostRequested);

      socket.off("room-token", onRoomToken);
      socket.off("join-denied", onJoinDenied);

      socket.off("chat-muted", onChatMuted);
      socket.off("muted", onMuted);

      socket.off("muted-roster-update", onMutedRosterUpdate);
      socket.off("muted-roster-snapshot", onMutedRosterSnapshot);

      socket.off("members-snapshot", onMembersSnapshot);
      socket.off("member-joined", onMemberJoined);
      socket.off("member-left", onMemberLeft);

      socket.off("kicked", onKicked);

      socket.off("unmute-requested", onUnmuteRequested);
      socket.off("unmute-request-status", onUnmuteRequestStatus);

      socket.off("set-video", onSetVideo);
      socket.off("sync-play", onSyncPlay);
      socket.off("sync-pause", onSyncPause);
      socket.off("sync-seek", onSyncSeek);

      // ✅ NEW: cleanup snapshot listener
      socket.off("chat-snapshot", onChatSnapshot);

      socket.off("chat-message", onChatMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost, mp4Ready, ytReady, video, room, navigate, roomId]);

  useEffect(() => {
    if (joined) socket.emit("request-sync", { room });
  }, [joined, room]);

  // Viewer polling drift correction
  useEffect(() => {
    if (!joined) return;
    if (isHost) return;
    if (video?.type === "twitch" || video?.type === "kick") return;

    const pollMs = video?.type === "youtube" && isIOS ? 2000 : 1000;

    const interval = setInterval(() => {
      if (!room) return;
      if (!video) return;

      socket.emit("get-state", { room }, (state) => {
        if (!state || !state.video) return;

        if (state.video?.url !== video?.url || state.video?.videoId !== video?.videoId) {
          setVideo(state.video);
          setMp4Ready(false);
          setYtReady(false);
          setPendingSync(null);
          return;
        }

        const { time, serverSentMs, isPlaying } = state;
        const target = isPlaying ? adjustedPlayTime(time, serverSentMs) : time;

        if (!isPlayerReady()) {
          setPendingSync({ time: target, shouldPlay: !!isPlaying });
          return;
        }

        const current = getCurrentTime();
        const drift = Math.abs(current - target);

        const driftThreshold = video.type === "mp4" ? 0.25 : isIOS ? 1.25 : 0.6;

        if (drift > driftThreshold) {
          seekTo(target);
        }

        const localPlaying = getLocalIsPlaying();
        if (isPlaying && !localPlaying) playLocal();
        if (!isPlaying && localPlaying) pauseLocal();
      });
    }, pollMs);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, isHost, room, video, mp4Ready, ytReady]);

  // YouTube player creator
  useEffect(() => {
    if (!video || video.type !== "youtube") return;

    const createPlayer = () => {
      // ✅ SAFETY: always cleanup first, without crashing React
      safeDestroyYouTubePlayer();

      const origin = getYouTubeOrigin();

      try {
        ytPlayerRef.current = new window.YT.Player("yt-player", {
          // ✅ Key change: use nocookie host
          host: "https://www.youtube-nocookie.com",

          videoId: video.videoId,

          origin,

          playerVars: {
            autoplay: 0,
            playsinline: 1,
            controls: isHost ? 1 : isIOS ? 1 : 0,
            disablekb: isHost ? 0 : 1,
            rel: 0,
            modestbranding: 1,
            origin,
            widget_referrer: origin,
          },

          events: {
            onReady: () => setYtReady(true),
            onStateChange: (e) => {
              if (!isHost) return;
              if (suppressHostEmitRef.current) return;

              const t = getCurrentTime();
              if (e.data === 1) socket.emit("play", { room, time: t });
              if (e.data === 2) socket.emit("pause", { room, time: t });
            },
            onError: (e) => {
              try {
                // eslint-disable-next-line no-console
                console.warn("YouTube Player error:", e?.data, e);
              } catch {
                // ignore
              }
              setYtReady(false);
              showToast(
                `YouTube error ${e?.data || "unknown"} — player blocked or failed.`,
                "error",
                3200
              );
            },
          },
        });
      } catch (e) {
        try {
          // eslint-disable-next-line no-console
          console.warn("YT Player create failed:", e);
        } catch {
          // ignore
        }
        setYtReady(false);
      }
    };

    if (window.YT && window.YT.Player) createPlayer();
    else window.onYouTubeIframeAPIReady = () => createPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video, isHost, room, isIOS, safeDestroyYouTubePlayer]);

const joinRoom = () => {
  const safeName = (name || "").trim();
  const safeRoom = normalizeRoomCode(room || "");
  if (!safeName || !safeRoom) return;

  // Clear stale room state before joining new room
  setVideo(null);
  setMp4Ready(false);
  setYtReady(false);
  setPendingSync(null);

  saveName(safeName);
  setRoom(safeRoom);

  const currentUrlRoom = normalizeRoomCode(roomId || "");

  if (currentUrlRoom && currentUrlRoom === safeRoom) {
    if (urlToken) saveToken(safeRoom, urlToken);
    setJoined(true);

    socket.emit("join-room", {
      room: safeRoom,
      name: safeName,
      token: (urlToken || readSavedToken(safeRoom)) || undefined,
      userId: userIdRef.current,
    });

    return;
  }

  navigate(`/room/${encodeURIComponent(safeRoom)}`);
};

  // ✅ FIXED: Copy/share uses PUBLIC_WEB_ORIGIN inside Capacitor app
const copyRoomLink = async () => {
  const safeRoom = normalizeRoomCode(room);
  if (!safeRoom) return;

  const origin = getShareOrigin();
  const base = `${origin}/room/${encodeURIComponent(safeRoom)}`;
  const link = isHost && roomToken
    ? `${base}?token=${encodeURIComponent(roomToken)}`
    : base;

  const fallbackCopy = () => {
    const ta = document.createElement("textarea");
    ta.value = link;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(ta);
    }
  };

  try {
    // ✅ NEW: native share sheet first
    if (navigator.share) {
      await navigator.share({
        title: "Join my WatchParty Room",
        text: "Come watch with me 👀",
        url: link,
      });
      return;
    }

    // existing copy logic
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(link);
    } else {
      fallbackCopy();
    }

    showToast("Room link copied.", "success");
  } catch (err) {
    // user cancelled share → do nothing
    if (err?.name === "AbortError") return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        fallbackCopy();
      }
      showToast("Room link copied.", "success");
    } catch {
      try {
        fallbackCopy();
        showToast("Room link copied.", "success");
      } catch {
        showToast("Copy failed.", "error", 2600);
      }
    }
  }
};

  // ✅ Host set link: reset first to force a clean unmount/mount (prevents DOM removeChild crash)
  const setVideoSafely = (v) => {
    // Step 1: reset state (forces player unmount)
    setVideo(null);
    setMp4Ready(false);
    setYtReady(false);
    setPendingSync(null);

    // Step 2: next tick set the new video + emit
    setTimeout(() => {
      setVideo(v);
      socket.emit("set-video", { room, video: v });
    }, 0);
  };

  const hostSetLink = (rawUrl) => {
    if (!isHost) return;

    const url = String(rawUrl || inputUrl || "").trim();
    if (!url) return;

    const result = classifyLink(url);

    // VIDEO PATH
    if (result.type === "video") {
      const ytId = getYouTubeId(url);
      if (ytId) {
        const v = { type: "youtube", url, videoId: ytId, theme: "red" };
        setVideoSafely(v);
        setInputUrl("");
        return;
      }

      const twitchChannel = getTwitchChannel(url);
      if (twitchChannel) {
        // ✅ ensure YT player is fully cleaned up before switching to twitch
        safeDestroyYouTubePlayer();

        const v = { type: "twitch", url, channel: twitchChannel, theme: "purple", live: true };
        setVideoSafely(v);
        setInputUrl("");
        return;
      }

      const kickChannel = getKickChannel(url);
      if (kickChannel) {
        safeDestroyYouTubePlayer();

        const v = { type: "kick", url, channel: kickChannel, theme: "green", live: true };
        setVideoSafely(v);
        setInputUrl("");
        return;
      }

      if (url.toLowerCase().endsWith(".mp4")) {
        safeDestroyYouTubePlayer();

        const v = { type: "mp4", url, videoId: null, theme: "red" };
        setVideoSafely(v);
        setInputUrl("");
        return;
      }
    }

    // SHOPPING PATH
    if (result.type === "shopping") {
      socket.emit("chat-message", { room, text: `🛒 ${url}` });
      showToast("Shopping link shared", "success");
      setInputUrl("");
      return;
    }

    showToast("Link not recognized", "error");
  };

  const hostSeek = () => {
    if (!isHost) return;
    if (video?.type === "twitch" || video?.type === "kick") return;
    socket.emit("seek", { room, time: getCurrentTime() });
  };

  const onMp4Play = () => {
    if (!isHost) return;
    if (suppressHostEmitRef.current) return;
    socket.emit("play", { room, time: getCurrentTime() });
  };

  const onMp4Pause = () => {
    if (!isHost) return;
    if (suppressHostEmitRef.current) return;
    socket.emit("pause", { room, time: getCurrentTime() });
  };

  const requestHost = () => {
    if (requestSent) return;
    socket.emit("request-host", { room });
    setRequestSent(true);
  };

  const grantHost = (newHostId) => {
    socket.emit("grant-host", { room, newHostId });
    setLastHostRequest(null);
  };

  const passHost = () => {
    socket.emit("pass-host", { room });
    setLastHostRequest(null);
  };

  if (!joined) {
    return (
      <>
        <Toast toast={toast} onClose={closeToast} />
        <Home
          logo={logo}
          name={name}
          setName={setName}
          room={room}
          setRoom={(v) => setRoom(normalizeRoomCode(v))}
          joinRoom={joinRoom}
          copyRoomLink={copyRoomLink}
        />
      </>
    );
  }

  // ✅ ShareLink should also use the public origin in-app
  const shareOrigin = getShareOrigin();
  const shareBase = `${shareOrigin}/room/${encodeURIComponent(room)}`;
  const shareLink =
    isHost && roomToken ? `${shareBase}?token=${encodeURIComponent(roomToken)}` : shareBase;

  return (
    <>
      <Toast toast={toast} onClose={closeToast} />
      <Room
        socket={socket}
        viewerTapToStart={viewerTapToStart}
        logo={logo}
        room={room}
        isHost={isHost}
        hostId={hostId}
        hostName={hostName}
        roomToken={roomToken}
        shareLink={shareLink}
        copyRoomLink={copyRoomLink}
        inputUrl={inputUrl}
        setInputUrl={setInputUrl}
        hostSetVideo={hostSetLink}
        video={video}
        mp4Ref={mp4Ref}
        setMp4Ready={setMp4Ready}
        onMp4Play={onMp4Play}
        onMp4Pause={onMp4Pause}
        hostSeek={hostSeek}
        chatMessages={chatMessages}
        chatInput={chatInput}
        setChatInput={setChatInput}
        sendChat={sendChat}
        chatEndRef={chatEndRef}
        mutedUsers={mutedUsers}
        muteUser={muteUser}
        unmuteUser={unmuteUser}
        isSelfMuted={isSelfMuted}
        requestUnmute={requestUnmute}
        unmuteStatusText={unmuteStatusText}
        roomMembers={roomMembers}
        mutedRoster={mutedRoster}
        lastHostRequest={lastHostRequest}
        setLastHostRequest={setLastHostRequest}
        grantHost={grantHost}
        passHost={passHost}
        requestHost={requestHost}
        requestSent={requestSent}
        unmuteRequests={unmuteRequests}
        approveUnmuteRequest={approveUnmuteRequest}
        ignoreUnmuteRequest={ignoreUnmuteRequest}
        kickUser={kickUser}
        showHostDrawer={showHostDrawer}
        setShowHostDrawer={setShowHostDrawer}
        // ✅ expose exit for an Exit button inside Room UI
        exitRoom={() => exitRoom({ navigateHome: true })}
      />
    </>
  );
}